import { useEffect, useMemo, useState } from "react";
import { Copy, Gift, Users, Share2, Trophy, MessageCircle, Mail, Send, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function Referral() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/referrals/me").then((r) => setData(r.data)),
      api.get("/referrals/leaderboard").then((r) => setBoard(r.data.top || [])).catch(() => {}),
    ]);
  }, []);

  const shareUrl = useMemo(() => {
    if (!data) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/register?ref=${data.referral_code}`;
  }, [data]);

  const message = useMemo(() => {
    if (!data) return "";
    if (data.role === "helper") {
      return `Salut ! Rejoins-moi sur Go Helper et gagne de l'argent en aidant des gens près de chez toi. Avec mon code de parrainage, on gagne 25 $ chacun à tes 3 premières tâches (et plus aux paliers 10 et 25). Inscris-toi ici : ${shareUrl}`;
    }
    return `Salut ! J'utilise Go Helper pour me faire aider rapidement par des helpers locaux. Avec mon code, on reçoit 10 $ de crédit chacun dès ta 1re tâche. Inscris-toi ici : ${shareUrl}`;
  }, [data, shareUrl]);

  if (!data) return <div className="text-navy">Chargement…</div>;

  const isHelper = data.role === "helper";
  const tiers = data.tiers || [];

  const copy = (text, label = "Copié !") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Rejoins-moi sur Go Helper",
          text: message,
          url: shareUrl,
        });
      } catch {/* user cancelled */}
    } else {
      copy(message, "Message copié — colle-le où tu veux !");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-3xl md:text-4xl font-heading font-black text-navy">Parrainage</h1>

      {/* HERO BLOCK */}
      <div className="bg-navy text-white rounded-3xl p-6 md:p-10 relative overflow-hidden noise" data-testid="ref-hero">
        <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-lime/20 blur-3xl" />
        <div className="relative">
          <Gift size={32} className="text-lime" />
          <h2 className="text-2xl md:text-3xl font-heading font-black mt-3">
            {isHelper
              ? <>Invite un helper. <span className="text-lime">Gagnez jusqu'à 175 $ chacun.</span></>
              : <>Invite un ami. <span className="text-lime">10 $ de crédit chacun.</span></>}
          </h2>
          <p className="text-white/80 mt-2 max-w-lg">
            {isHelper
              ? "Quand ton filleul complète des tâches, des bonus se débloquent automatiquement aux paliers 3, 10 et 25."
              : "Quand ton filleul publie sa 1re tâche, tu reçois 10 $ de crédit utilisable sur ta prochaine demande."}
          </p>

          <div className="mt-6 inline-flex items-center gap-2 bg-white/10 rounded-full p-2 pl-5 backdrop-blur">
            <span className="font-mono font-bold tracking-widest text-lime text-xl" data-testid="ref-code">{data.referral_code}</span>
            <button onClick={() => copy(data.referral_code, "Code copié !")}
              className="bg-lime text-navy font-bold rounded-full px-4 py-2 flex items-center gap-1" data-testid="copy-code">
              <Copy size={14} /> Copier
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={nativeShare} className="bg-lime text-navy font-bold rounded-full px-5 py-2.5 flex items-center gap-2 hover:scale-105 transition-transform" data-testid="share-native">
              <Share2 size={16} /> Partager
            </button>
            <a href={`sms:?&body=${encodeURIComponent(message)}`} className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2.5 flex items-center gap-2 backdrop-blur" data-testid="share-sms">
              <MessageCircle size={16} /> SMS
            </a>
            <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer"
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2.5 flex items-center gap-2 backdrop-blur" data-testid="share-whatsapp">
              <Send size={16} /> WhatsApp
            </a>
            <a href={`https://www.facebook.com/dialog/send?app_id=&link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer"
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2.5 flex items-center gap-2 backdrop-blur" data-testid="share-messenger">
              <Send size={16} /> Messenger
            </a>
            <a href={`mailto:?subject=Rejoins Go Helper&body=${encodeURIComponent(message)}`}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2.5 flex items-center gap-2 backdrop-blur" data-testid="share-email">
              <Mail size={16} /> Email
            </a>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid md:grid-cols-3 gap-4">
        {isHelper ? (
          <div className="card-surface p-6">
            <div className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">Bonus gagné</div>
            <div className="font-heading font-black text-4xl text-navy mt-2" data-testid="bonus-earned">
              {data.bonus_earned.toFixed(2)} $
            </div>
            <div className="text-xs text-[#475569] mt-1">Versé sur tes paiements</div>
          </div>
        ) : (
          <div className="card-surface p-6 ring-2 ring-lime/40">
            <div className="text-xs font-black uppercase tracking-wider text-[#94A3B8] flex items-center gap-1">
              <Wallet size={12} /> Crédits Go Helper
            </div>
            <div className="font-heading font-black text-4xl text-navy mt-2" data-testid="client-credits">
              {data.client_credits.toFixed(2)} $
            </div>
            <div className="text-xs text-[#475569] mt-1">Appliqués automatiquement à ta prochaine tâche</div>
          </div>
        )}
        <div className="card-surface p-6">
          <div className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">Filleuls</div>
          <div className="font-heading font-black text-4xl text-navy mt-2" data-testid="children-count">
            {data.children.length}
          </div>
        </div>
        <div className="card-surface p-6">
          <div className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">Lien magique</div>
          <button onClick={() => copy(shareUrl, "Lien copié !")}
            className="mt-2 inline-flex items-center gap-1 text-navy font-bold text-sm hover:underline truncate max-w-full" data-testid="copy-link">
            <Copy size={14} /> {shareUrl.replace(/^https?:\/\//, "")}
          </button>
        </div>
      </div>

      {/* PROGRESSIVE TIERS (helpers only) */}
      {isHelper && tiers.length > 1 && (
        <div className="card-surface p-6" data-testid="ref-tiers">
          <h3 className="font-heading font-black text-xl text-navy flex items-center gap-2">
            <Trophy size={20} className="text-lime" /> Paliers progressifs par filleul
          </h3>
          <p className="text-sm text-[#475569] mt-1">Plus ton filleul travaille, plus vous gagnez tous les deux.</p>
          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            {tiers.map((t, i) => (
              <div key={t.threshold} className={`rounded-xl p-4 border-2 ${i === 0 ? "border-lime bg-lime/10" : "border-[#E2E8F0]"}`}>
                <div className="text-xs font-black uppercase tracking-wider text-[#94A3B8]">Palier {i + 1}</div>
                <div className="font-heading font-black text-2xl text-navy mt-1">{t.threshold} tâches</div>
                <div className="text-lime font-black text-lg mt-1">+ {t.bonus.toFixed(0)} $ chacun</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CHILDREN LIST */}
      <div className="card-surface p-6">
        <h3 className="font-heading font-black text-xl text-navy flex items-center gap-2">
          <Users size={20} /> Mes filleuls
        </h3>
        {data.children.length === 0 ? (
          <p className="mt-4 text-[#475569]">
            Aucun filleul pour l'instant. <strong>Partage ton code</strong> avec tes amis pour commencer à gagner !
          </p>
        ) : (
          <div className="mt-4 space-y-2">
            {data.children.map((c) => {
              const target = isHelper ? 3 : 1;
              const progress = Math.min(100, ((c.tasks_count || 0) / target) * 100);
              const done = c.bonus_paid || c.tier_reached >= 1;
              return (
                <div key={c.id} className="py-3 border-b border-[#E2E8F0] last:border-0" data-testid={`child-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-navy text-lime font-black flex items-center justify-center shrink-0">
                        {c.child_name[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-navy truncate">{c.child_name}</div>
                        <div className="text-xs text-[#475569]">
                          {c.tasks_count}/{target} tâche{target > 1 ? "s" : ""}
                          {isHelper && c.tier_reached > 0 && <> · Palier {c.tier_reached} atteint</>}
                        </div>
                      </div>
                    </div>
                    {done ? (
                      <span className="chip-lime flex items-center gap-1"><CheckCircle2 size={12} /> Bonus reçu</span>
                    ) : (
                      <span className="chip">En cours</span>
                    )}
                  </div>
                  <div className="mt-2 h-1.5 bg-surface rounded-full overflow-hidden">
                    <div className="h-full bg-lime transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* LEADERBOARD */}
      {board.length > 0 && (
        <div className="card-surface p-6" data-testid="ref-leaderboard">
          <h3 className="font-heading font-black text-xl text-navy flex items-center gap-2">
            <Trophy size={20} className="text-lime" /> Top parrains du mois
          </h3>
          <p className="text-sm text-[#475569] mt-1">Le top 3 du mois reçoit un bonus surprise de 100 $ 🎁</p>
          <div className="mt-4 space-y-2">
            {board.map((row) => (
              <div key={row.rank} className={`flex items-center gap-3 py-2 px-3 rounded-xl ${row.is_me ? "bg-lime/20 ring-2 ring-lime" : ""}`} data-testid={`board-row-${row.rank}`}>
                <div className={`w-8 h-8 rounded-full font-black flex items-center justify-center text-sm ${row.rank === 1 ? "bg-yellow-400 text-navy" : row.rank === 2 ? "bg-slate-300 text-navy" : row.rank === 3 ? "bg-amber-700 text-white" : "bg-surface text-navy"}`}>
                  {row.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy truncate">{row.name} {row.is_me && <span className="chip-lime ml-1">Toi</span>}</div>
                  <div className="text-xs text-[#475569]">{row.children_count} filleul{row.children_count > 1 ? "s" : ""} · {row.role}</div>
                </div>
                <div className="text-lime font-black">{row.total_bonus.toFixed(0)} $</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
